import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Badge, Form, InputGroup } from 'react-bootstrap';
import { db } from '../../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import AccessNotice from '../Portal/AccessNotice';
import { isPermissionDenied } from '../../utils/permissions';
import { OUTCOME_META, OUTCOME_ORDER, isOutcome } from '../../services/lessonOutcome';

/**
 * A student's lesson history, read from the session records in `lessons`.
 *
 * RETIRED HERE 2026-09-01 (attendance arc, Phase A, card fwMubDlBzniA3JQtbUGi):
 * this component's own `attendanceStatus` vocabulary and edit modal — eight
 * values (present / absent / absent-makeup-issued / teacher-canceled-no-makeup
 * …), written by nothing else and read by nothing else. Measured before
 * removal: 1,534 lesson docs in prod, ZERO carrying the field. It was a second
 * language for attendance waiting to be found, so it is gone rather than left.
 *
 * Attendance is now the record's `outcome` (services/lessonOutcome.js), marked
 * from Today or the Lesson Dashboard. This view only READS it — the mark lives
 * where the lesson is taught, not in a history modal.
 *
 * Still React-Bootstrap: the Student Profile page it sits on is legacy, and
 * "touch it, modernize it" applies to the page, not to one panel on it.
 */

// The record's outcome → a react-bootstrap Badge variant. Every outcome tone in
// OUTCOME_META has a mapping here; a missing/unknown value reads as Unmarked.
const TONE_TO_VARIANT = { success: 'success', danger: 'danger', neutral: 'secondary', warning: 'warning', info: 'info' };

const outcomeBadge = (outcome) => {
	if (outcome && isOutcome(outcome)) {
		const m = OUTCOME_META[outcome];
		return (
			<Badge bg={TONE_TO_VARIANT[m.tone] || 'secondary'}>
				<i className={`fa-sharp fa-solid ${m.icon} me-1`}></i>
				{m.label}
			</Badge>
		);
	}
	return (
		<Badge bg='light' text='dark'>
			<i className='fa-sharp fa-solid fa-clock me-1'></i>
			Unmarked
		</Badge>
	);
};

function StudentLessonsHistory({ studentId }) {
	const navigate = useNavigate();
	const [lessons, setLessons] = useState([]);
	const [filteredLessons, setFilteredLessons] = useState([]);
	const [loading, setLoading] = useState(true);
	const [permDenied, setPermDenied] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [statusFilter, setStatusFilter] = useState('all'); // all | <outcome> | unmarked
	const [dateFilter, setDateFilter] = useState('all'); // all, past, upcoming

	useEffect(() => {
		if (studentId) {
			fetchLessons();
		}
	}, [studentId]);

	useEffect(() => {
		applyFilters();
	}, [lessons, searchTerm, statusFilter, dateFilter]);

	const fetchLessons = async () => {
		try {
			setLoading(true);

			const lessonsQuery = query(
				collection(db, 'lessons'),
				where('studentId', '==', studentId),
				orderBy('date', 'desc'),
				orderBy('time', 'desc')
			);

			const snapshot = await getDocs(lessonsQuery);
			setLessons(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
		} catch (error) {
			console.error('Error fetching lessons:', error);
			// A rules denial must not read as "No lessons found" (#27).
			if (isPermissionDenied(error)) setPermDenied(true);
		} finally {
			setLoading(false);
		}
	};

	const applyFilters = () => {
		let filtered = [...lessons];

		// Date filter
		const today = new Date().toISOString().split('T')[0];
		if (dateFilter === 'past') {
			filtered = filtered.filter((lesson) => lesson.date < today);
		} else if (dateFilter === 'upcoming') {
			filtered = filtered.filter((lesson) => lesson.date >= today);
		}

		// Outcome filter. "unmarked" is the absence of a valid outcome.
		if (statusFilter === 'unmarked') {
			filtered = filtered.filter((lesson) => !isOutcome(lesson.outcome));
		} else if (statusFilter !== 'all') {
			filtered = filtered.filter((lesson) => lesson.outcome === statusFilter);
		}

		// Search filter
		if (searchTerm) {
			const searchLower = searchTerm.toLowerCase();
			filtered = filtered.filter((lesson) =>
				(lesson.date || '').includes(searchLower) ||
				(lesson.time || '').includes(searchLower) ||
				(lesson.location && lesson.location.toLowerCase().includes(searchLower)) ||
				(lesson.outcomeNote && lesson.outcomeNote.toLowerCase().includes(searchLower))
			);
		}

		setFilteredLessons(filtered);
	};

	const formatDate = (dateStr) => {
		if (!dateStr) return '';
		const date = new Date(dateStr + 'T00:00:00');
		return date.toLocaleDateString('en-US', {
			weekday: 'short',
			month: 'short',
			day: 'numeric',
			year: 'numeric'
		});
	};

	const formatTime = (time24) => {
		if (!time24) return '';
		const [hours, minutes] = time24.split(':');
		const hour = parseInt(hours);
		const ampm = hour >= 12 ? 'PM' : 'AM';
		const hour12 = hour % 12 || 12;
		return `${hour12}:${minutes} ${ampm}`;
	};

	// Statistics — counted, never presumed: a lesson with no outcome is Unmarked,
	// not attended (Anthony, 2026-09-01).
	const stats = {
		total: lessons.length,
		attended: lessons.filter((l) => l.outcome === 'attended').length,
		noShow: lessons.filter((l) => l.outcome === 'no_show').length,
		unmarked: lessons.filter((l) => !isOutcome(l.outcome)).length,
	};

	return (
		<Card className='shadow-sm'>
			<Card.Header className='bg-transparent'>
				<h5 className='mb-0'>
					<i className='fa-sharp fa-solid fa-calendar-check me-2' style={{ color: '#384971' }}></i>
					Lesson History
				</h5>
			</Card.Header>
			<Card.Body>
				{/* Statistics */}
				<div className='row g-2 mb-4'>
					<div className='col-md-3 col-6'>
						<Card className='border-0 bg-light text-center'>
							<Card.Body className='py-2'>
								<h4 className='mb-0' style={{ color: '#384971' }}>{stats.total}</h4>
								<small className='text-muted'>Total</small>
							</Card.Body>
						</Card>
					</div>
					<div className='col-md-3 col-6'>
						<Card className='border-0 bg-light text-center'>
							<Card.Body className='py-2'>
								<h4 className='mb-0' style={{ color: '#28a745' }}>{stats.attended}</h4>
								<small className='text-muted'>Attended</small>
							</Card.Body>
						</Card>
					</div>
					<div className='col-md-3 col-6'>
						<Card className='border-0 bg-light text-center'>
							<Card.Body className='py-2'>
								<h4 className='mb-0' style={{ color: '#dc3545' }}>{stats.noShow}</h4>
								<small className='text-muted'>No-show</small>
							</Card.Body>
						</Card>
					</div>
					<div className='col-md-3 col-6'>
						<Card className='border-0 bg-light text-center'>
							<Card.Body className='py-2'>
								<h4 className='mb-0' style={{ color: '#6c757d' }}>{stats.unmarked}</h4>
								<small className='text-muted'>Unmarked</small>
							</Card.Body>
						</Card>
					</div>
				</div>

				{/* Filters */}
				<div className='row g-3 mb-4'>
					<div className='col-md-5'>
						<InputGroup size='sm'>
							<InputGroup.Text>
								<i className='fa-sharp fa-solid fa-search'></i>
							</InputGroup.Text>
							<Form.Control
								type='text'
								placeholder='Search lessons...'
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
							/>
						</InputGroup>
					</div>
					<div className='col-md-3'>
						<Form.Select
							size='sm'
							value={dateFilter}
							onChange={(e) => setDateFilter(e.target.value)}
						>
							<option value='all'>All Dates</option>
							<option value='upcoming'>Upcoming</option>
							<option value='past'>Past</option>
						</Form.Select>
					</div>
					<div className='col-md-4'>
						<Form.Select
							size='sm'
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
						>
							<option value='all'>All outcomes</option>
							{OUTCOME_ORDER.map((v) => (
								<option key={v} value={v}>{OUTCOME_META[v].label}</option>
							))}
							<option value='unmarked'>Unmarked</option>
						</Form.Select>
					</div>
				</div>

				{/* Lessons Table */}
				{loading ? (
					<div className='text-center py-4'>
						<i className='fa-sharp fa-solid fa-spinner fa-spin fa-2x' style={{ color: '#384971' }}></i>
						<p className='mt-2 text-muted'>Loading lessons...</p>
					</div>
				) : permDenied ? (
					<AccessNotice what="this student's lessons" />
				) : filteredLessons.length === 0 ? (
					<div className='text-center py-5 text-muted'>
						<i className='fa-sharp fa-solid fa-calendar-xmark fa-3x mb-3 d-block' style={{ opacity: 0.3 }}></i>
						<p>No lessons found</p>
					</div>
				) : (
					<div className='table-responsive'>
						<Table hover className='mb-0'>
							<thead className='table-light'>
								<tr>
									<th>Date</th>
									<th>Time</th>
									<th>Location</th>
									<th>Attendance</th>
										<th className='text-end'>Lesson</th>
								</tr>
							</thead>
							<tbody>
								{filteredLessons.map((lesson) => (
									<tr key={lesson.id}>
										<td className='align-middle'>
											<strong>{formatDate(lesson.date)}</strong>
										</td>
										<td className='align-middle'>{formatTime(lesson.time)}</td>
										<td className='align-middle'>
											<span className='badge bg-light text-dark'>{lesson.location || 'N/A'}</span>
										</td>
										<td className='align-middle'>
											{outcomeBadge(lesson.outcome)}
											{lesson.outcomeNote && (
												<div className='mt-1'>
													<small className='text-muted'>
														<i className='fa-sharp fa-solid fa-sticky-note me-1'></i>
														{lesson.outcomeNote}
													</small>
												</div>
											)}
										</td>
											{/* Every row is a session record (`lessons/<id>`), so every row
											    opens in the Lesson Dashboard — the path back into a past
											    lesson from the student's own page (Anthony, 2026-09-06: a
											    pick-up conversation, or something remembered later, needs
											    a way to the lesson AND to leave feedback). "Recap" lands on
											    the recap drawer directly. */}
											<td className='align-middle text-end text-nowrap'>
												<button
													type='button'
													className='btn btn-sm btn-outline-secondary me-1'
													title='Open this lesson in the Lesson Dashboard'
													onClick={() => navigate(`/portal/work/lesson/${lesson.id}`)}>
													<i className='fa-sharp fa-solid fa-book-open me-1'></i>Open
												</button>
												<button
													type='button'
													className='btn btn-sm btn-outline-secondary'
													title='Open this lesson with the recap drawer up — leave feedback for the student'
													onClick={() => navigate(`/portal/work/lesson/${lesson.id}?open=recap`)}>
													<i className='fa-sharp fa-solid fa-comment-dots me-1'></i>Recap
												</button>
											</td>
									</tr>
								))}
							</tbody>
						</Table>
					</div>
				)}
			</Card.Body>
		</Card>
	);
}

export default StudentLessonsHistory;
